import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import type { WidgetControl } from '@examdojo/core/widget';
import type { CoordinateSystemConfig } from '@examdojo/core/widget';
import type { GraphpadDisplayConfig, GraphpadParams, GraphpadWidgetConfig } from '../graphpad.model';
import { ElementListSectionComponent } from './sections/element-list-section.component';

@Component({
  selector: 'dojo-graphpad-editor',
  standalone: true,
  imports: [ElementListSectionComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './graphpad-editor.component.html',
  styleUrl: './graphpad-editor.component.scss',
  host: { class: 'block h-full' },
})
export class GraphpadEditorComponent {
  readonly config = input.required<GraphpadWidgetConfig>();
  readonly configChange = output<GraphpadWidgetConfig>();

  readonly params = computed(() => this.config().params);
  readonly display = computed(() => this.config().display);
  readonly coordSystem = computed(() => this.config().display?.coordinateSystem);
  readonly behaviorControls = computed(() => this.config().behavior?.controls ?? []);

  onCoordSystemChange(coordSystem: CoordinateSystemConfig): void {
    this.emitUpdate((draft) => {
      draft.display = { ...(draft.display ?? {}), coordinateSystem: coordSystem };
    });
  }

  onParamsChange(params: GraphpadParams): void {
    this.emitUpdate((draft) => {
      draft.params = params;
    });
  }

  onDisplayChange(display: GraphpadDisplayConfig): void {
    this.emitUpdate((draft) => {
      draft.display = { ...(draft.display ?? {}), ...display };
    });
  }

  onBehaviorControlsChange(controls: WidgetControl[]): void {
    this.emitUpdate((draft) => {
      draft.behavior = { ...(draft.behavior ?? {}), controls };
    });
  }

  private emitUpdate(mutate: (draft: GraphpadWidgetConfig) => void): void {
    const draft = structuredClone(this.config());
    mutate(draft);
    this.configChange.emit(draft);
  }
}
